import {
    faBinoculars,
    faCalendarWeek,
    faCamera,
    faEarthAmericas,
    faFeatherPointed,
    faFire,
    faLeaf,
    faLocationDot,
    faMap,
    faMountainSun,
    faSun,
    faTree
} from "@fortawesome/free-solid-svg-icons";

import type {AchievementDashboard, AchievementSetDefinition, AchievementTheme} from "./types";

export const ACHIEVEMENT_SEASON_LABELS: Record<number, string> = {
    1: 'Winter (Jan–Mar)',
    2: 'Spring (Apr–Jun)',
    3: 'Summer (Jul–Sep)',
    4: 'Fall (Oct–Dec)'
};

export const ACHIEVEMENTS_ROUTE = '/discover/achievements';

export const ACHIEVEMENT_THEME_ROUTES: Record<AchievementTheme, string> = {
    species: `${ACHIEVEMENTS_ROUTE}/species`,
    habitat: `${ACHIEVEMENTS_ROUTE}/habitats`,
    regions: `${ACHIEVEMENTS_ROUTE}/places`,
    challenge: `${ACHIEVEMENTS_ROUTE}/challenges`,
    media: `${ACHIEVEMENTS_ROUTE}/media`
};

const createMilestone = (
    prefix: string,
    threshold: number,
    description: string,
    rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary',
    points: number,
    title?: string
) => ({
    id: `${prefix}-${threshold}`,
    title: title ?? `${threshold.toLocaleString()} ${description}`,
    description: `Reach ${threshold.toLocaleString()} ${description.toLowerCase()}.`,
    threshold,
    rarity,
    points
});

const createFamiliarFacesMilestone = (threshold: number, rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary', points: number) => ({
    ...createMilestone(
        'familiar',
        threshold,
        'Regulars',
        rarity,
        points,
        `${threshold.toLocaleString()} Regulars on ${threshold.toLocaleString()} Dates Each`
    ),
    description: `Record ${threshold.toLocaleString()} species on at least ${threshold.toLocaleString()} distinct dates each.`,
    metricId: `familiarRegulars${threshold}` as 'familiarRegulars10' | 'familiarRegulars25' | 'familiarRegulars50' | 'familiarRegulars100'
});

export const ACHIEVEMENT_THEME_LABELS: AchievementDashboard['themeOptions'] = [
    {id: 'all', label: 'All Themes'},
    {id: 'species', label: 'Species'},
    {id: 'habitat', label: 'Habitats'},
    {id: 'regions', label: 'Places'},
    {id: 'challenge', label: 'Challenges'},
    {id: 'media', label: 'Media'}
];

export const ACHIEVEMENT_SET_DEFINITIONS: AchievementSetDefinition[] = [
    {
        id: 'species-explorer',
        title: 'Species Explorer',
        description: 'Discover and record unique species across your life list.',
        theme: 'species',
        metricId: 'species',
        color: '#5a7d43',
        icon: faBinoculars,
        milestones: [
            createMilestone('species', 50, 'Species', 'common', 80, 'First 50 Species'),
            createMilestone('species', 100, 'Species', 'common', 100),
            createMilestone('species', 200, 'Species', 'common', 130),
            createMilestone('species', 250, 'Species', 'common', 150),
            createMilestone('species', 300, 'Species', 'uncommon', 170),
            createMilestone('species', 400, 'Species', 'uncommon', 200),
            createMilestone('species', 500, 'Species', 'uncommon', 230),
            createMilestone('species', 600, 'Species', 'rare', 270),
            createMilestone('species', 750, 'Species', 'rare', 320),
            createMilestone('species', 1000, 'Species', 'rare', 380, '1,000 Species'),
            createMilestone('species', 1500, 'Species', 'epic', 470, '1,500 Species'),
            createMilestone('species', 2000, 'Species', 'epic', 580, '2,000 Species'),
            createMilestone('species', 2500, 'Species', 'epic', 700, '2,500 Species'),
            createMilestone('species', 3000, 'Species', 'legendary', 850, '3,000 Species'),
            createMilestone('species', 4000, 'Species', 'legendary', 1050, '4,000 Species'),
            createMilestone('species', 5000, 'Species', 'legendary', 1300, '5,000 Species'),
            createMilestone('species', 7500, 'Species', 'legendary', 1700, '7,500 Species'),
            createMilestone('species', 10000, 'Species', 'legendary', 2200, '10,000 Species')
        ]
    },
    {
        id: 'habitat-explorer',
        title: 'Habitat Explorer',
        description: 'Explore a wider spread of habitats across your birding life.',
        theme: 'habitat',
        metricId: 'habitats',
        color: '#567d6e',
        icon: faLeaf,
        milestones: [
            createMilestone('habitat', 5, 'Habitats', 'common', 60),
            createMilestone('habitat', 10, 'Habitats', 'common', 80),
            createMilestone('habitat', 15, 'Habitats', 'uncommon', 100),
            createMilestone('habitat', 20, 'Habitats', 'uncommon', 130),
            createMilestone('habitat', 25, 'Habitats', 'rare', 160),
            createMilestone('habitat', 30, 'Habitats', 'rare', 200),
            createMilestone('habitat', 35, 'Habitats', 'rare', 250),
            createMilestone('habitat', 40, 'Habitats', 'epic', 310),
            createMilestone('habitat', 45, 'Habitats', 'epic', 380),
            createMilestone('habitat', 50, 'Habitats', 'legendary', 460)
        ]
    },
    {
        id: 'biome-explorer',
        title: 'Biome Explorer',
        description: 'Explore the broad ecological communities that shape bird life around the world.',
        theme: 'habitat',
        metricId: 'biomes',
        color: '#3f7c85',
        icon: faTree,
        milestones: [
            createMilestone('biome', 2, 'Biomes', 'common', 60),
            createMilestone('biome', 3, 'Biomes', 'common', 80),
            createMilestone('biome', 5, 'Biomes', 'uncommon', 110),
            createMilestone('biome', 7, 'Biomes', 'rare', 150),
            createMilestone('biome', 10, 'Biomes', 'epic', 220),
            createMilestone('biome', 12, 'Biomes', 'epic', 290),
            createMilestone('biome', 15, 'Biomes', 'legendary', 380)
        ]
    },
    {
        id: 'ecoregion-explorer',
        title: 'Ecoregion Explorer',
        description: 'Visit distinct ecoregions and broaden the ecological reach of your birding.',
        theme: 'habitat',
        metricId: 'ecoregions',
        color: '#8a6c3e',
        icon: faMap,
        milestones: [
            createMilestone('ecoregion', 5, 'Ecoregions', 'common', 65),
            createMilestone('ecoregion', 10, 'Ecoregions', 'common', 90),
            createMilestone('ecoregion', 25, 'Ecoregions', 'uncommon', 130),
            createMilestone('ecoregion', 50, 'Ecoregions', 'rare', 190),
            createMilestone('ecoregion', 75, 'Ecoregions', 'rare', 250),
            createMilestone('ecoregion', 100, 'Ecoregions', 'epic', 330),
            createMilestone('ecoregion', 150, 'Ecoregions', 'epic', 430),
            createMilestone('ecoregion', 200, 'Ecoregions', 'legendary', 560)
        ]
    },
    {
        id: 'family-finder',
        title: 'Family Finder',
        description: 'Find birds across more and more avian families.',
        theme: 'species',
        metricId: 'families',
        color: '#6d5a80',
        icon: faFeatherPointed,
        milestones: [
            createMilestone('family', 5, 'Families', 'common', 55),
            createMilestone('family', 10, 'Families', 'common', 70),
            createMilestone('family', 25, 'Families', 'uncommon', 110),
            createMilestone('family', 50, 'Families', 'uncommon', 160),
            createMilestone('family', 75, 'Families', 'rare', 220),
            createMilestone('family', 100, 'Families', 'rare', 290),
            createMilestone('family', 150, 'Families', 'epic', 380),
            createMilestone('family', 200, 'Families', 'epic', 500),
            createMilestone('family', 250, 'Families', 'legendary', 650)
        ]
    },
    {
        id: 'genus-collector',
        title: 'Genus Collector',
        description: 'Record birds from an increasingly broad range of genera.',
        theme: 'species',
        metricId: 'genera',
        color: '#567d6e',
        icon: faBinoculars,
        milestones: [
            createMilestone('genus', 50, 'Genera', 'common', 70),
            createMilestone('genus', 75, 'Genera', 'common', 90),
            createMilestone('genus', 100, 'Genera', 'uncommon', 120),
            createMilestone('genus', 150, 'Genera', 'uncommon', 160),
            createMilestone('genus', 200, 'Genera', 'rare', 210),
            createMilestone('genus', 250, 'Genera', 'rare', 270),
            createMilestone('genus', 300, 'Genera', 'rare', 340),
            createMilestone('genus', 350, 'Genera', 'epic', 420),
            createMilestone('genus', 400, 'Genera', 'epic', 510),
            createMilestone('genus', 450, 'Genera', 'epic', 610),
            createMilestone('genus', 500, 'Genera', 'epic', 720),
            createMilestone('genus', 600, 'Genera', 'legendary', 850),
            createMilestone('genus', 700, 'Genera', 'legendary', 1000),
            createMilestone('genus', 800, 'Genera', 'legendary', 1170),
            createMilestone('genus', 900, 'Genera', 'legendary', 1360),
            createMilestone('genus', 1000, 'Genera', 'legendary', 1560, '1,000 Genera')
        ]
    },
    {
        id: 'year-round-regular',
        title: 'Year-Round Regular',
        description: 'Record the same species in all four calendar quarters.',
        theme: 'species',
        metricId: 'yearRoundSpecies',
        color: '#3f7c85',
        icon: faCalendarWeek,
        milestones: [
            createMilestone('year-round', 1, 'Species', 'common', 60, 'First Year-Round Species'),
            createMilestone('year-round', 10, 'Species', 'common', 100),
            createMilestone('year-round', 25, 'Species', 'uncommon', 160),
            createMilestone('year-round', 50, 'Species', 'rare', 250),
            createMilestone('year-round', 75, 'Species', 'rare', 320),
            createMilestone('year-round', 100, 'Species', 'epic', 400),
            createMilestone('year-round', 150, 'Species', 'epic', 520),
            createMilestone('year-round', 200, 'Species', 'legendary', 660),
            createMilestone('year-round', 250, 'Species', 'legendary', 820),
            createMilestone('year-round', 300, 'Species', 'legendary', 1000)
        ]
    },
    {
        id: 'lifer-rush',
        title: 'Lifer Rush',
        description: 'Find multiple life birds on the same calendar day.',
        theme: 'species',
        metricId: 'liferRush',
        color: '#b85f3f',
        icon: faFeatherPointed,
        milestones: [
            createMilestone('lifer-rush', 5, 'Life Birds in One Day', 'common', 80),
            createMilestone('lifer-rush', 10, 'Life Birds in One Day', 'uncommon', 140),
            createMilestone('lifer-rush', 15, 'Life Birds in One Day', 'rare', 220),
            createMilestone('lifer-rush', 20, 'Life Birds in One Day', 'epic', 320),
            createMilestone('lifer-rush', 25, 'Life Birds in One Day', 'legendary', 450),
            createMilestone('lifer-rush', 30, 'Life Birds in One Day', 'legendary', 580),
            createMilestone('lifer-rush', 40, 'Life Birds in One Day', 'legendary', 740),
            createMilestone('lifer-rush', 50, 'Life Birds in One Day', 'legendary', 920),
            createMilestone('lifer-rush', 60, 'Life Birds in One Day', 'legendary', 1120),
            createMilestone('lifer-rush', 70, 'Life Birds in One Day', 'legendary', 1340),
            createMilestone('lifer-rush', 100, 'Life Birds in One Day', 'legendary', 1650)
        ]
    },
    {
        id: 'taxonomic-tour',
        title: 'Taxonomic Tour',
        description: 'Record species from many different families on one checklist.',
        theme: 'species',
        metricId: 'checklistFamilies',
        color: '#6d5a80',
        icon: faFeatherPointed,
        milestones: [
            createMilestone('taxonomic-tour', 5, 'Families on One Checklist', 'common', 80),
            createMilestone('taxonomic-tour', 10, 'Families on One Checklist', 'uncommon', 140),
            createMilestone('taxonomic-tour', 15, 'Families on One Checklist', 'rare', 220),
            createMilestone('taxonomic-tour', 20, 'Families on One Checklist', 'epic', 320),
            createMilestone('taxonomic-tour', 25, 'Families on One Checklist', 'legendary', 450),
            createMilestone('taxonomic-tour', 30, 'Families on One Checklist', 'legendary', 600)
        ]
    },
    {
        id: 'familiar-faces',
        title: 'Familiar Faces',
        description: 'Build a roster of regular species, each recorded on the required number of distinct calendar dates.',
        theme: 'species',
        metricId: 'familiarRegulars10',
        color: '#5a7d43',
        icon: faBinoculars,
        milestones: [
            createFamiliarFacesMilestone(10, 'common', 80),
            createFamiliarFacesMilestone(25, 'uncommon', 180),
            createFamiliarFacesMilestone(50, 'rare', 340),
            createFamiliarFacesMilestone(100, 'legendary', 650)
        ]
    },
    {
        id: 'county-lister',
        title: 'County Lister',
        description: 'Expand your reach one county at a time.',
        theme: 'regions',
        metricId: 'counties',
        color: '#b56d2c',
        icon: faLocationDot,
        milestones: [
            createMilestone('county', 5, 'Counties', 'common', 60),
            createMilestone('county', 10, 'Counties', 'common', 80),
            createMilestone('county', 25, 'Counties', 'common', 110),
            createMilestone('county', 50, 'Counties', 'uncommon', 150),
            createMilestone('county', 100, 'Counties', 'uncommon', 220),
            createMilestone('county', 150, 'Counties', 'rare', 300),
            createMilestone('county', 200, 'Counties', 'rare', 390),
            createMilestone('county', 250, 'Counties', 'rare', 490),
            createMilestone('county', 300, 'Counties', 'epic', 600),
            createMilestone('county', 400, 'Counties', 'epic', 760),
            createMilestone('county', 500, 'Counties', 'legendary', 950),
            createMilestone('county', 750, 'Counties', 'legendary', 1250),
            createMilestone('county', 1000, 'Counties', 'legendary', 1650)
        ]
    },
    {
        id: 'state-province-lister',
        title: 'State/Province Lister',
        description: 'Expand your birding footprint across states and provinces.',
        theme: 'regions',
        metricId: 'stateProvinces',
        color: '#b56d2c',
        icon: faLocationDot,
        milestones: [
            createMilestone('state-province', 5, 'States/Provinces', 'common', 60),
            createMilestone('state-province', 10, 'States/Provinces', 'common', 80),
            createMilestone('state-province', 25, 'States/Provinces', 'common', 110),
            createMilestone('state-province', 50, 'States/Provinces', 'uncommon', 150),
            createMilestone('state-province', 75, 'States/Provinces', 'uncommon', 190),
            createMilestone('state-province', 100, 'States/Provinces', 'rare', 240),
            createMilestone('state-province', 150, 'States/Provinces', 'rare', 320),
            createMilestone('state-province', 200, 'States/Provinces', 'rare', 410),
            createMilestone('state-province', 250, 'States/Provinces', 'epic', 520),
            createMilestone('state-province', 300, 'States/Provinces', 'epic', 650),
            createMilestone('state-province', 350, 'States/Provinces', 'epic', 790),
            createMilestone('state-province', 400, 'States/Provinces', 'legendary', 950),
            createMilestone('state-province', 500, 'States/Provinces', 'legendary', 1200)
        ]
    },
    {
        id: 'world-traveler',
        title: 'World Traveler',
        description: 'Bird in countries near and far.',
        theme: 'regions',
        metricId: 'countries',
        color: '#3b7384',
        icon: faEarthAmericas,
        milestones: [
            createMilestone('country', 2, 'Countries', 'common', 70),
            createMilestone('country', 3, 'Countries', 'common', 90),
            createMilestone('country', 5, 'Countries', 'uncommon', 120),
            createMilestone('country', 10, 'Countries', 'uncommon', 170),
            createMilestone('country', 15, 'Countries', 'rare', 230),
            createMilestone('country', 20, 'Countries', 'rare', 300),
            createMilestone('country', 25, 'Countries', 'rare', 380),
            createMilestone('country', 50, 'Countries', 'epic', 520),
            createMilestone('country', 75, 'Countries', 'epic', 700),
            createMilestone('country', 100, 'Countries', 'legendary', 920),
            createMilestone('country', 150, 'Countries', 'legendary', 1250),
            createMilestone('country', 200, 'Countries', 'legendary', 1650)
        ]
    },
    {
        id: 'big-year-chaser',
        title: 'Big Year',
        description: 'Push your strongest year higher and higher.',
        theme: 'challenge',
        metricId: 'bigYearSpecies',
        color: '#9b5142',
        icon: faMountainSun,
        milestones: [
            createMilestone('bigyear', 50, 'Species Year', 'common', 60, '50 Species Year'),
            createMilestone('bigyear', 100, 'Species Year', 'common', 90, '100 Species Year'),
            createMilestone('bigyear', 150, 'Species Year', 'common', 120, '150 Species Year'),
            createMilestone('bigyear', 200, 'Species Year', 'uncommon', 160, '200 Species Year'),
            createMilestone('bigyear', 250, 'Species Year', 'uncommon', 210, '250 Species Year'),
            createMilestone('bigyear', 300, 'Species Year', 'uncommon', 270, '300 Species Year'),
            createMilestone('bigyear', 350, 'Species Year', 'rare', 340, '350 Species Year'),
            createMilestone('bigyear', 400, 'Species Year', 'rare', 420, '400 Species Year'),
            createMilestone('bigyear', 450, 'Species Year', 'rare', 510, '450 Species Year'),
            createMilestone('bigyear', 500, 'Species Year', 'epic', 610, '500 Species Year'),
            createMilestone('bigyear', 550, 'Species Year', 'epic', 720, '550 Species Year'),
            createMilestone('bigyear', 600, 'Species Year', 'epic', 840, '600 Species Year'),
            createMilestone('bigyear', 650, 'Species Year', 'epic', 970, '650 Species Year'),
            createMilestone('bigyear', 700, 'Species Year', 'epic', 1110, '700 Species Year'),
            createMilestone('bigyear', 750, 'Species Year', 'epic', 1260, '750 Species Year'),
            createMilestone('bigyear', 800, 'Species Year', 'legendary', 1420, '800 Species Year'),
            createMilestone('bigyear', 850, 'Species Year', 'legendary', 1590, '850 Species Year'),
            createMilestone('bigyear', 900, 'Species Year', 'legendary', 1770, '900 Species Year'),
            createMilestone('bigyear', 950, 'Species Year', 'legendary', 1960, '950 Species Year'),
            createMilestone('bigyear', 1000, 'Species Year', 'legendary', 2160, '1,000 Species Year'),
            createMilestone('bigyear', 1500, 'Species Year', 'legendary', 2700, '1,500 Species Year'),
            createMilestone('bigyear', 2000, 'Species Year', 'legendary', 3400, '2,000 Species Year')
        ]
    },
    {
        id: 'birding-streak',
        title: 'Birding Streak',
        description: 'Keep your momentum going with a checklist on consecutive days.',
        theme: 'challenge',
        metricId: 'birdingStreak',
        color: '#b85f3f',
        icon: faFire,
        milestones: [
            createMilestone('streak', 7, 'Consecutive Days', 'common', 70, '7-Day Streak'),
            createMilestone('streak', 14, 'Consecutive Days', 'common', 95, '14-Day Streak'),
            createMilestone('streak', 30, 'Consecutive Days', 'uncommon', 140, '30-Day Streak'),
            createMilestone('streak', 60, 'Consecutive Days', 'uncommon', 190, '60-Day Streak'),
            createMilestone('streak', 90, 'Consecutive Days', 'rare', 250, '90-Day Streak'),
            createMilestone('streak', 120, 'Consecutive Days', 'rare', 320, '120-Day Streak'),
            createMilestone('streak', 150, 'Consecutive Days', 'rare', 390, '150-Day Streak'),
            createMilestone('streak', 180, 'Consecutive Days', 'epic', 480, '180-Day Streak'),
            createMilestone('streak', 365, 'Consecutive Days', 'epic', 650, '365-Day Streak'),
            createMilestone('streak', 500, 'Consecutive Days', 'legendary', 850, '500-Day Streak'),
            createMilestone('streak', 1000, 'Consecutive Days', 'legendary', 1200, '1,000-Day Streak'),
            createMilestone('streak', 1500, 'Consecutive Days', 'legendary', 1650, '1,500-Day Streak')
        ]
    },
    {
        id: 'big-day',
        title: 'Big Day',
        description: 'Record as many species as possible during one calendar day.',
        theme: 'challenge',
        metricId: 'bigDaySpecies',
        color: '#d17a2e',
        icon: faSun,
        milestones: [
            createMilestone('bigday', 50, 'Species in One Day', 'common', 90, '50-Species Day'),
            createMilestone('bigday', 75, 'Species in One Day', 'common', 120, '75-Species Day'),
            createMilestone('bigday', 100, 'Species in One Day', 'uncommon', 160, '100-Species Day'),
            createMilestone('bigday', 150, 'Species in One Day', 'rare', 260, '150-Species Day'),
            createMilestone('bigday', 200, 'Species in One Day', 'epic', 400, '200-Species Day'),
            createMilestone('bigday', 250, 'Species in One Day', 'legendary', 600, '250-Species Day')
        ]
    },
    {
        id: 'big-checklist',
        title: 'Big Checklist',
        description: 'Record an increasing number of unique species on one checklist.',
        theme: 'challenge',
        metricId: 'bigChecklist',
        color: '#d17a2e',
        icon: faBinoculars,
        milestones: [
            createMilestone('big-checklist', 25, 'Species on One Checklist', 'common', 90),
            createMilestone('big-checklist', 50, 'Species on One Checklist', 'common', 130),
            createMilestone('big-checklist', 75, 'Species on One Checklist', 'uncommon', 190),
            createMilestone('big-checklist', 100, 'Species on One Checklist', 'rare', 270),
            createMilestone('big-checklist', 125, 'Species on One Checklist', 'epic', 370),
            createMilestone('big-checklist', 150, 'Species on One Checklist', 'legendary', 500)
        ]
    },
    {
        id: 'weekend-warrior',
        title: 'Weekend Warrior',
        description: 'Build a year of birding weekends, each with at least one checklist.',
        theme: 'challenge',
        metricId: 'weekendWarrior',
        color: '#7b6544',
        icon: faCalendarWeek,
        milestones: [
            createMilestone('weekend', 10, 'Distinct Weekends', 'common', 90),
            createMilestone('weekend', 20, 'Distinct Weekends', 'common', 140),
            createMilestone('weekend', 26, 'Distinct Weekends', 'uncommon', 190),
            createMilestone('weekend', 30, 'Distinct Weekends', 'rare', 250),
            createMilestone('weekend', 40, 'Distinct Weekends', 'epic', 360),
            createMilestone('weekend', 52, 'Distinct Weekends', 'legendary', 520)
        ]
    },
    {
        id: 'photographer',
        title: 'Photographer',
        description: 'Build a portfolio by photographing unique bird species.',
        theme: 'media',
        metricId: 'photoSpecies',
        color: '#486575',
        icon: faCamera,
        milestones: [
            createMilestone('photo', 10, 'Species Photographed', 'common', 70),
            createMilestone('photo', 25, 'Species Photographed', 'common', 95),
            createMilestone('photo', 50, 'Species Photographed', 'uncommon', 130),
            createMilestone('photo', 100, 'Species Photographed', 'uncommon', 180),
            createMilestone('photo', 250, 'Species Photographed', 'rare', 260),
            createMilestone('photo', 500, 'Species Photographed', 'rare', 360),
            createMilestone('photo', 750, 'Species Photographed', 'epic', 480),
            createMilestone('photo', 1000, 'Species Photographed', 'epic', 620),
            createMilestone('photo', 1500, 'Species Photographed', 'legendary', 820),
            createMilestone('photo', 2000, 'Species Photographed', 'legendary', 1080)
        ]
    },
    {
        id: 'photo-coverage',
        title: 'Photo Coverage',
        description: 'Photograph an increasing share of the species on your life list.',
        theme: 'media',
        metricId: 'photoCoverage',
        color: '#486575',
        icon: faCamera,
        milestones: [
            createMilestone('photo-coverage', 10, '10% of Life List Photographed', 'common', 70, '10% Life List Photographed'),
            createMilestone('photo-coverage', 25, '25% of Life List Photographed', 'common', 95, '25% Life List Photographed'),
            createMilestone('photo-coverage', 30, '30% of Life List Photographed', 'uncommon', 120, '30% Life List Photographed'),
            createMilestone('photo-coverage', 40, '40% of Life List Photographed', 'uncommon', 160, '40% Life List Photographed'),
            createMilestone('photo-coverage', 50, '50% of Life List Photographed', 'rare', 220, '50% Life List Photographed'),
            createMilestone('photo-coverage', 60, '60% of Life List Photographed', 'rare', 290, '60% Life List Photographed'),
            createMilestone('photo-coverage', 75, '75% of Life List Photographed', 'epic', 400, '75% Life List Photographed'),
            createMilestone('photo-coverage', 90, '90% of Life List Photographed', 'epic', 540, '90% Life List Photographed'),
            createMilestone('photo-coverage', 100, '100% of Life List Photographed', 'legendary', 720, '100% Life List Photographed')
        ]
    },
    {
        id: 'family-album',
        title: 'Family Album',
        description: 'Photograph at least one species from more and more avian families.',
        theme: 'media',
        metricId: 'photoFamilies',
        color: '#6d5a80',
        icon: faCamera,
        milestones: [
            createMilestone('photo-family', 5, 'Families Photographed', 'common', 55),
            createMilestone('photo-family', 10, 'Families Photographed', 'common', 70),
            createMilestone('photo-family', 25, 'Families Photographed', 'uncommon', 110),
            createMilestone('photo-family', 50, 'Families Photographed', 'uncommon', 160),
            createMilestone('photo-family', 75, 'Families Photographed', 'rare', 220),
            createMilestone('photo-family', 100, 'Families Photographed', 'rare', 290),
            createMilestone('photo-family', 150, 'Families Photographed', 'epic', 380),
            createMilestone('photo-family', 200, 'Families Photographed', 'epic', 500),
            createMilestone('photo-family', 250, 'Families Photographed', 'legendary', 650)
        ]
    },
    {
        id: 'illustrated-checklist',
        title: 'Illustrated Checklist',
        description: 'Photograph an increasing number of different species on a single checklist.',
        theme: 'media',
        metricId: 'illustratedChecklist',
        color: '#486575',
        icon: faCamera,
        milestones: [
            createMilestone('illustrated-checklist', 5, 'Species Photographed on One Checklist', 'common', 80),
            createMilestone('illustrated-checklist', 10, 'Species Photographed on One Checklist', 'common', 120),
            createMilestone('illustrated-checklist', 15, 'Species Photographed on One Checklist', 'uncommon', 170),
            createMilestone('illustrated-checklist', 20, 'Species Photographed on One Checklist', 'rare', 240),
            createMilestone('illustrated-checklist', 25, 'Species Photographed on One Checklist', 'rare', 320),
            createMilestone('illustrated-checklist', 30, 'Species Photographed on One Checklist', 'epic', 410),
            createMilestone('illustrated-checklist', 40, 'Species Photographed on One Checklist', 'epic', 530),
            createMilestone('illustrated-checklist', 50, 'Species Photographed on One Checklist', 'legendary', 680)
        ]
    },
    {
        id: 'lifer-lens',
        title: 'Lifer Lens',
        description: 'Photograph life birds on the calendar date you first record them.',
        theme: 'media',
        metricId: 'liferLens',
        color: '#486575',
        icon: faCamera,
        milestones: [
            createMilestone('lifer-lens', 1, 'Lifer Photographed on First Observation', 'common', 60),
            createMilestone('lifer-lens', 5, 'Lifers Photographed on First Observation', 'common', 90),
            createMilestone('lifer-lens', 10, 'Lifers Photographed on First Observation', 'uncommon', 140),
            createMilestone('lifer-lens', 25, 'Lifers Photographed on First Observation', 'rare', 220),
            createMilestone('lifer-lens', 50, 'Lifers Photographed on First Observation', 'rare', 310),
            createMilestone('lifer-lens', 75, 'Lifers Photographed on First Observation', 'epic', 410),
            createMilestone('lifer-lens', 100, 'Lifers Photographed on First Observation', 'epic', 530),
            createMilestone('lifer-lens', 150, 'Lifers Photographed on First Observation', 'legendary', 680),
            createMilestone('lifer-lens', 200, 'Lifers Photographed on First Observation', 'legendary', 850),
            createMilestone('lifer-lens', 250, 'Lifers Photographed on First Observation', 'legendary', 1050),
            createMilestone('lifer-lens', 300, 'Lifers Photographed on First Observation', 'legendary', 1280),
            createMilestone('lifer-lens', 400, 'Lifers Photographed on First Observation', 'legendary', 1540),
            createMilestone('lifer-lens', 500, 'Lifers Photographed on First Observation', 'legendary', 1840)
        ]
    }
];
